import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState, store } from './store';
import { BookModel } from '../interfaces/book.interface';
import { ISettingsBookAge } from '../interfaces/settings.interface';
import { PlanDetailsModel } from '../interfaces/plan.interface';
import { ArtStyleInfo } from '../interfaces/story.interface';
import { Dictionary } from '../interfaces/_common.interface';
import { getURLSearch } from '../helper/helper';

export type TypePlansSubscription = 'monthly' | 'yearly';

export interface IPlan {
  id: string | number;
  name: string;
}

export interface IPlanPurchaseModalData {
  title?: string;
  description?: string;
  unclosable?: boolean;
  showLogOut?: boolean;
}

export interface ICompleteProfileModelData {
  title?: string;
  description?: string;
  unclosable?: boolean;
  showLogOut?: boolean;
}

export enum SelectedMainCharacterPopupEnum {
  NEW = 'new',
  EXIST = 'exist',
  SUBMIT = 'submit',
}

interface AppState {
  appKey: number;
  currentPathname: string;
  currentSearchQuery: string;
  currentMainURLQuery: string;
  beforeUnloadEvent: BeforeUnloadEvent | null;

  draftId: string | null;
  bookId: string | null;
  bookData: BookModel | null;
  bookDataModified: boolean;
  currentStep: number;
  startNewStory: boolean;
  isTitleModalOpen: boolean;
  bookSettingsAge: ISettingsBookAge | null;
  isPlanPurchaseModalVisible: boolean;
  showShareModalData: BookModel | null;
  planPurchaseModalData: IPlanPurchaseModalData | null;
  openIsStudentBooksCompleted: boolean;

  isCompleteProfileModalVisible: boolean;
  completeProfileModalData: ICompleteProfileModelData | null;
  uploadImageExamplePopup: 'open' | 'submit' | null;
  ensureToUseSelectedMainCharacterPopup: {
    previous: SelectedMainCharacterPopupEnum | null;
    current: SelectedMainCharacterPopupEnum | null;
  };

  isFinishStory: boolean;
  listPlansToSubscribe: PlanDetailsModel[] | null;
  typePlansSubscription: TypePlansSubscription;

  allUserPlans: IPlan[];
  artStyleById: Dictionary<string>;
}

const initialState: AppState = {
  appKey: new Date().getTime(),
  currentPathname: '',
  currentSearchQuery: getURLSearch(true),
  currentMainURLQuery: getURLSearch(false),
  beforeUnloadEvent: null,
  draftId: null,
  bookId: null,
  bookData: null,
  bookDataModified: false,
  currentStep: 0,
  startNewStory: true,
  isTitleModalOpen: false,
  bookSettingsAge: null,
  isPlanPurchaseModalVisible: false,
  isCompleteProfileModalVisible: false,
  completeProfileModalData: null,
  openIsStudentBooksCompleted: false,
  uploadImageExamplePopup: null,
  ensureToUseSelectedMainCharacterPopup: {
    previous: null,
    current: null,
  },
  showShareModalData: null,
  planPurchaseModalData: null,
  isFinishStory: false,
  listPlansToSubscribe: null,
  typePlansSubscription: 'monthly',
  allUserPlans: [],
  artStyleById: {},
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setAppKey(state, action: PayloadAction<number>) {
      state.appKey = action.payload;
    },
    setCurrentPathname(state, action: PayloadAction<string>) {
      state.currentPathname = action.payload;
    },
    setCurrentSearchQuery(state, action: PayloadAction<string>) {
      state.currentSearchQuery = action.payload;
    },
    setCurrentMainURLQuery(state, action: PayloadAction<string>) {
      state.currentMainURLQuery = action.payload;
    },
    setBeforeUnloadEvent(state, action: PayloadAction<{ event: BeforeUnloadEvent }>) {
      state.beforeUnloadEvent = action.payload?.event;
    },
    setDraftId(state, action: PayloadAction<string | null>) {
      state.draftId = action.payload;
    },
    setBookId(state, action: PayloadAction<string | null>) {
      state.bookId = action.payload;
      if (!state.bookId) {
        state.bookData = null;
      }
    },
    setBookData(state, action: PayloadAction<any>) {
      state.bookData = action.payload;
    },
    updateBookData(state, action: PayloadAction<Partial<BookModel>>) {
      state.bookData = { ...state.bookData, ...action.payload };
    },
    setBookDataModified(state, action: PayloadAction<boolean>) {
      state.bookDataModified = action.payload;
    },
    setCurrentStep(state, action: PayloadAction<number>) {
      state.currentStep = action.payload;
    },
    setStartNewStory(state, action: PayloadAction<boolean>) {
      state.startNewStory = action.payload;
    },
    setIsTitleModalOpen(state, action: PayloadAction<boolean>) {
      state.isTitleModalOpen = action.payload;
    },
    setBookSettingsAge(state, action: PayloadAction<ISettingsBookAge>) {
      state.bookSettingsAge = action.payload;
    },
    openPlanPurchaseModal(
      state,
      action: PayloadAction<{
        show: boolean;
        isFinishStory?: boolean;
        data?: IPlanPurchaseModalData;
      }>,
    ) {
      state.isPlanPurchaseModalVisible = action.payload.show;
      state.planPurchaseModalData = action.payload.data || null;
      state.isFinishStory = action.payload.isFinishStory || false;
    },
    openStudentBooksCompletedPopup(
      state,
      action: PayloadAction<{
        show: boolean;
      }>,
    ) {
      state.openIsStudentBooksCompleted = action.payload.show;
    },
    openIsCompleteProfileModalVisible(
      state,
      action: PayloadAction<{
        show: boolean;
        data?: ICompleteProfileModelData;
      }>,
    ) {
      state.isCompleteProfileModalVisible = action.payload.show;
      state.completeProfileModalData = action.payload.data || null;
    },
    setlistPlansToSubscribe(state, action: PayloadAction<PlanDetailsModel[]>) {
      state.listPlansToSubscribe = action.payload;
    },
    setTypePlansSubscription(state, action: PayloadAction<TypePlansSubscription>) {
      state.typePlansSubscription = action.payload;
    },
    setAllUserPlansList(state, action: PayloadAction<IPlan[]>) {
      state.allUserPlans = action.payload;
    },
    setShowShareModalData(state, action: PayloadAction<BookModel | null>) {
      state.showShareModalData = action.payload;
    },
    setUploadImageExamplePopup(state, action: PayloadAction<'open' | 'submit' | null>) {
      state.uploadImageExamplePopup = action.payload;
    },
    setEnsureToUseSelectedMainCharacterPopup(state, action: PayloadAction<SelectedMainCharacterPopupEnum | null>) {
      state.ensureToUseSelectedMainCharacterPopup = {
        previous: state.ensureToUseSelectedMainCharacterPopup
          ? state.ensureToUseSelectedMainCharacterPopup.current
          : null,
        current: action.payload,
      };
    },
    setArtStyles(state, action: PayloadAction<ArtStyleInfo[]>) {
      state.artStyleById = action.payload?.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {} as Dictionary<string>);
    },
  },
});

export const {
  setAppKey,
  setCurrentPathname,
  setCurrentSearchQuery,
  setCurrentMainURLQuery,
  setBeforeUnloadEvent,
  setDraftId,
  setBookId,
  setBookData,
  updateBookData,
  setBookDataModified,
  setCurrentStep,
  setStartNewStory,
  setIsTitleModalOpen,
  setBookSettingsAge,
  openPlanPurchaseModal,
  openStudentBooksCompletedPopup,
  openIsCompleteProfileModalVisible,
  setlistPlansToSubscribe,
  setTypePlansSubscription,
  setAllUserPlansList,
  setShowShareModalData,
  setArtStyles,
  setUploadImageExamplePopup,
  setEnsureToUseSelectedMainCharacterPopup,
} = appSlice.actions;

export const selectApp = (state: RootState) => state.app;

export const appStore = () => {
  const dataStore = selectApp(store.getState());

  return { ...dataStore };
};

export default appSlice.reducer;
